/**
 * HQ 입시 컨설팅 API 서비스.
 *
 * 백엔드: backend/domains/hq/api/hq_admission.py
 *  - /api/v1/hq/admission/match-v3       (정시 매칭)
 *  - /api/v1/hq/admission/match-susi     (수시 매칭)
 *  - /api/v1/hq/admission/trend          (5년 트렌드)
 *  - /api/v1/hq/admission/changes        (변경사항 검색)
 *  - /api/v1/hq/admission/insights       (전체 인사이트)
 *  - /api/v1/hq/admission/insights/medical-trend
 *  - /api/v1/hq/admission/insights/ai-bio-new
 *  - /api/v1/hq/admission/stats          (메타정보)
 */
import hqApi from './hqApiClient';

const BASE = '/api/v1/hq/admission';

// ─────────────────────────────────────────────────────────
// 타입 정의
// ─────────────────────────────────────────────────────────
export interface MatchResult {
  univ_cd: string;
  univ: string;
  jeonhyeong_type: string;
  jeonhyeong?: string;
  gun?: string;
  dept: string;
  capacity_2025?: number | null;
  competition_2025?: number | null;
  p70_avg_3y?: number | null;
  p70_avg_5y?: number | null;
  p70_avg_2025?: number | null;
  cut70_grade_3y?: number | null;
  cut70_grade_5y?: number | null;
  cut70_grade_2025?: number | null;
  trend?: TrendPoint[];
  diff: number;
  grade: '안정' | '적정' | '도전';
  // 9칸 시스템
  kan?: number;
  probability?: number;
  kan_label?: string;
  kan_color?: 'blue' | 'green' | 'yellow' | 'orange' | 'red';
}

export interface TrendPoint {
  year: number;
  p70_avg?: number | null;
  cut70_score?: number | null;
  cut70_grade?: number | null;
  capacity?: number | null;
}

export interface MatchResponse {
  mode: string;
  strategy?: string;
  student_percentile?: number;
  student_grade?: number;
  total_matched: number;
  by_grade: { 적정: number; 안정: number; 도전: number };
  by_kan?: Record<string, number>;
  results: MatchResult[];
}

export interface ChangeItem {
  univ: string;
  result_year: string;
  category: string;
  sub_category: string;
  content: string;
  severity: string;
  affects_medical: string;
  affects_ai_bio: string;
}

export interface ChangesResponse {
  filters: Record<string, unknown>;
  total: number;
  results: ChangeItem[];
}

export interface InsightsResponse {
  summary: {
    total: number;
    by_year: Record<string, number>;
    by_severity: Record<string, number>;
    by_category: Record<string, number>;
  };
  highlights: {
    medical: ChangeItem[];
    ai_bio: ChangeItem[];
    capacity: ChangeItem[];
    admission_method: ChangeItem[];
  };
}

export interface AdmissionStats {
  schema: string;
  total_records: number;
  universities: number;
  by_jeonhyeong_type: Record<string, number>;
}

// ─────────────────────────────────────────────────────────
// API 호출
// ─────────────────────────────────────────────────────────
export const hqAdmissionService = {
  /** 메타 정보 */
  async stats(): Promise<AdmissionStats> {
    const r = await hqApi.get(`${BASE}/stats`);
    return r.data;
  },

  /** 대학 목록 */
  async listUniversities(): Promise<{ count: number; universities: string[] }> {
    const r = await hqApi.get(`${BASE}/universities`);
    return r.data;
  },

  /** 정시 매칭 */
  async matchJeongsi(params: {
    percentile: number;
    strategy?: '3y' | '5y' | '2025';
    gun?: string;
    limit?: number;
  }): Promise<MatchResponse> {
    const r = await hqApi.get(`${BASE}/match-v3`, { params });
    return r.data;
  },

  /** 정시 영역별 점수 매칭 (Phase 1) */
  async matchByScores(body: {
    korean_score?: number | null;
    math_score?: number | null;
    english_grade?: number | null;
    inquiry1_subject?: string | null;
    inquiry1_score?: number | null;
    inquiry2_subject?: string | null;
    inquiry2_score?: number | null;
    korean_history_grade?: number | null;
    strategy?: '3y' | '5y' | '2025';
    gun?: string | null;
    limit?: number;
  }): Promise<{
    evaluated: Record<string, any>;
    average_percentile: number;
    average_grade: number | null;
    n_subjects: number;
    match: MatchResponse;
  }> {
    const r = await hqApi.post(`${BASE}/match-by-scores`, body);
    return r.data;
  },

  /** 사용 가능한 영역×유형 목록 */
  async listSubjects(): Promise<{ subjects: { key: string; subject: string; type: string; max_score: number; min_score: number }[] }> {
    const r = await hqApi.get(`${BASE}/subjects`);
    return r.data;
  },

  /** 수시 매칭 */
  async matchSusi(params: {
    grade: number;
    jeonhyeong: '학생부교과' | '학생부종합';
    strategy?: '3y' | '5y' | '2025';
    limit?: number;
  }): Promise<MatchResponse> {
    const r = await hqApi.get(`${BASE}/match-susi`, { params });
    return r.data;
  },

  /** 학과 검색 */
  async search(query: string, limit = 30): Promise<{ count: number; results: MatchResult[] }> {
    const r = await hqApi.get(`${BASE}/search`, { params: { q: query, limit } });
    return r.data;
  },

  /** 학과별 5년 트렌드 */
  async trend(univ: string, dept = '', jeonhyeong?: string) {
    const r = await hqApi.get(`${BASE}/trend`, { params: { univ, dept, jeonhyeong } });
    return r.data;
  },

  /** 변경사항 검색 */
  async searchChanges(params: {
    univ?: string;
    year?: number;
    category?: string;
    severity?: string;
    affects_medical?: boolean;
    affects_ai_bio?: boolean;
    keyword?: string;
    limit?: number;
  }): Promise<ChangesResponse> {
    const r = await hqApi.get(`${BASE}/changes`, { params });
    return r.data;
  },

  /** 변경사항 카테고리 목록 */
  async changeCategories(): Promise<{ categories: { category: string; count: number }[] }> {
    const r = await hqApi.get(`${BASE}/changes/categories`);
    return r.data;
  },

  /** 종합 인사이트 */
  async insights(): Promise<InsightsResponse> {
    const r = await hqApi.get(`${BASE}/insights`);
    return r.data;
  },

  /** 의대/약대 트렌드 */
  async medicalTrend() {
    const r = await hqApi.get(`${BASE}/insights/medical-trend`);
    return r.data;
  },

  /** AI/바이오 신설 학과 */
  async aiBioNew() {
    const r = await hqApi.get(`${BASE}/insights/ai-bio-new`);
    return r.data;
  },
};

export default hqAdmissionService;
